// Routes and the session bootstrap. Signed out → the sign-in screen; signed in → Portal, Worlds, and the per-world screens.
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { onSignedOut } from "@/api/http";
import { useSession } from "@/state/session";
import { usePrompts } from "@/state/prompts";
import { DialogHost, Toaster } from "@/kit/overlays";
import { SignIn } from "@/screens/auth/SignIn";
import { Closed } from "@/screens/auth/Closed";
import { Worlds } from "@/screens/worlds/Worlds";
import { Portal } from "@/screens/portal/Portal";
import { WorldLanding } from "@/app/WorldLanding";
import { ComingSoon } from "@/screens/placeholder/ComingSoon";
import { MapScreen } from "@/screens/map/MapScreen";
import { KitPage } from "@/screens/kit/KitPage";
import { WorldRoute } from "./WorldRoute";
import { CityRoute } from "@/screens/city/CityRoute";
import { CityView } from "@/screens/city/CityView";
import { CityHall } from "@/screens/city/CityHall";
import { Building } from "@/screens/city/Building";
import { Forge } from "@/screens/city/Forge";
import { Recruit } from "@/screens/city/Recruit";
import { Academy } from "@/screens/city/Academy";
import { Rally } from "@/screens/city/Rally";
import { Market } from "@/screens/city/Market";
import { Store } from "@/screens/city/Store";
import { StoreRedirect } from "@/screens/city/StoreRedirect";
import { Wheel } from "@/screens/city/Wheel";
import { Loyalty } from "@/screens/city/Loyalty";
import { Settings } from "@/screens/settings/Settings";
import { Reports } from "@/screens/reports/Reports";
import { PublicReport } from "@/screens/reports/PublicReport";
import { Mail } from "@/screens/mail/Mail";
import { GuildScreen } from "@/screens/guild/GuildScreen";
import { Leaderboards } from "@/screens/rank/Leaderboards";
import { PlayerProfile } from "@/screens/profile/PlayerProfile";
import { Titles } from "@/screens/profile/Titles";
import { Quests } from "@/screens/quests/Quests";
import { Help } from "@/screens/help/Help";
import { useApplyPrefs } from "@/state/prefs";

export function App() {
  const status = useSession((s) => s.status);
  const setSignedIn = useSession((s) => s.setSignedIn);
  const setSignedOut = useSession((s) => s.setSignedOut);
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();
  // Valor Web shut to players (Oct 22 2026): the Worker says so on /api/session; only shared report links still open
  const [closed, setClosed] = useState(false);
  useApplyPrefs();

  // who is signed in on this device (the Worker reads the cookie)
  useEffect(() => {
    let alive = true;
    api.session
      .get()
      .then((s) => {
        if (!alive) return;
        if (s.closed) {
          setClosed(true);
          setSignedOut();
          return;
        }
        if (s.signedIn && s.uid) setSignedIn({ uid: s.uid, login: s.login, email: s.email });
        else setSignedOut();
      })
      .catch(() => alive && setSignedOut());
    return () => {
      alive = false;
    };
  }, [setSignedIn, setSignedOut]);

  // the Worker said the session is gone
  useEffect(
    () =>
      onSignedOut(() => {
        if (useSession.getState().status === "in") usePrompts.getState().toast("You've been signed out.", "error");
        setSignedOut();
        qc.clear();
      }),
    [setSignedOut, qc],
  );

  useEffect(() => {
    // a shared report (/r/<id>, Oct 15 2026) opens for anyone, signed in or not
    if (closed) return;
    if (status === "out" && !loc.pathname.startsWith("/sign-in") && loc.pathname !== "/kit" && !loc.pathname.startsWith("/r/")) nav("/sign-in", { replace: true });
  }, [closed, status, loc.pathname, nav]);

  if (status === "unknown") {
    return (
      <div className="shell" aria-busy="true">
        <main className="shell-main no-nav center" style={{ display: "grid", placeItems: "center", minHeight: "60dvh" }}>
          <span className="spin" style={{ fontSize: 28 }} />
        </main>
      </div>
    );
  }

  if (closed) {
    return (
      <>
        <Routes>
          <Route path="/r/:id" element={<PublicReport />} />
          <Route path="*" element={<Closed />} />
        </Routes>
        <DialogHost />
        <Toaster />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/kit" element={<KitPage />} />
        <Route path="/r/:id" element={<PublicReport />} />
        {status === "in" && (
          <>
            <Route path="/" element={<Navigate to="/portal" replace />} />
            <Route path="/portal" element={<Portal />} />
            <Route path="/worlds" element={<Worlds />} />
            <Route path="/w/:wid" element={<WorldRoute />}>
              <Route index element={<WorldLanding />} />
              {/* Empire was retired Oct 9 2026: old links land on the current city */}
              <Route path="empire" element={<WorldLanding />} />
              <Route path="map" element={<MapScreen />} />
              {/* one route each, so on a tablet / PC the list beside the open item stays put (Reports.tsx, Mail.tsx) */}
              <Route path="reports/:id?" element={<Reports />} />
              <Route path="mail/:id?" element={<Mail />} />
              <Route path="guild" element={<GuildScreen />} />
              <Route path="rank" element={<Leaderboards />} />
              <Route path="leaderboards" element={<Navigate to="../rank" replace />} />
              <Route path="player/:uid" element={<PlayerProfile />} />
              <Route path="titles" element={<Titles />} />
              <Route path="quests" element={<Quests />} />
              <Route path="store" element={<StoreRedirect />} />
              <Route path="city/:vid" element={<CityRoute />}>
                <Route index element={<CityView />} />
                <Route path="hall" element={<CityHall />} />
                <Route path="b/:key" element={<Building />} />
                <Route path="forge" element={<Forge />} />
                <Route path="recruit/:building" element={<Recruit />} />
                <Route path="academy" element={<Academy />} />
                <Route path="rally/:tab?" element={<Rally />} />
                <Route path="market/:tab?" element={<Market />} />
                <Route path="store/:tab?" element={<Store />} />
                <Route path="wheel" element={<Wheel />} />
                <Route path="loyalty" element={<Loyalty />} />
                <Route path="*" element={<ComingSoon title="City" phase="Phase 1" />} />
              </Route>
              <Route path="*" element={<ComingSoon title="Soon" phase="a later phase" />} />
            </Route>
            <Route path="/settings" element={<Settings />} />
            <Route path="/help" element={<Help />} />
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </>
        )}
        {status === "out" && <Route path="*" element={<Navigate to="/sign-in" replace />} />}
      </Routes>
      <Toaster />
      <DialogHost />
    </>
  );
}
